import { useCallback, useEffect, useState } from 'react';

/**
 * Toggle real browser fullscreen for the whole app while keeping every normal
 * control interactive — unlike projector mode, which is a locked-down kiosk
 * overlay that exits on any click/keypress. Stays in sync with the browser so
 * leaving fullscreen via Esc/F11 flips the button back too.
 */
export function useFullscreen(): { isFullscreen: boolean; toggleFullscreen: () => void } {
  const [isFullscreen, setIsFullscreen] = useState(() => Boolean(document.fullscreenElement));

  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    // requestFullscreen/exitFullscreen must be called from a user gesture; the
    // optional-chaining guards Safari/older browsers that lack the API.
    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => undefined);
    } else {
      document.documentElement.requestFullscreen?.().catch(() => undefined);
    }
  }, []);

  return { isFullscreen, toggleFullscreen };
}
